import React, { useState } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { cueFormSchema } from "./formSchema";
import { cueUiSchema } from "./uiSchema";
import { Container, Paper, Box, Button, TextField, Typography, CircularProgress } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { IChangeEvent } from "@rjsf/core";
import { RJSFSchema } from "@rjsf/utils";
import styles from "../form.module.css";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import PhoneWidget from "../widgets/PhoneWidget";
import { convertJSONToMultiPartFormData } from "../utils/general";
import { host } from "config";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";

const fileFields = [];

const widgets = {
  PhoneWidget: PhoneWidget,
};

// Custom field for the blue separator bar
const BlueSeparatorField = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#1a365d",
        height: "50px",
        mb: 3,
        mx: -4,
      }}
    />
  );
};

// Custom Object Field Template to control layout
const CueObjectFieldTemplate = (props) => {
  const { properties } = props;

  return (
    <Box>
      {properties.map((element, index) => {
        const fieldName = element.content.props.name;

        // Skip hidden fields
        if (["firstName", "lastName", "email", "phone"].includes(fieldName)) {
          return null;
        }

        // Render other fields normally
        return (
          <Box key={index} sx={{ mb: 2 }}>
            {element.content}
          </Box>
        );
      })}
    </Box>
  );
};

// Custom fields
const fields = {
  // nameField: NameField,
  // emailPhoneField: EmailPhoneField,
  blueSeparatorField: BlueSeparatorField,
};

// Custom templates
const templates = {
  ObjectFieldTemplate: CueObjectFieldTemplate,
};

function CueForm() {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = (data: IChangeEvent<FormData>) => {
    setIsLoading(true);
    fetch(`${host}/api/implementation/forms/cue`, {
      method: "POST",
      body: convertJSONToMultiPartFormData(data.formData, fileFields),
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        }
        response.json().then(async (data: any) => {
          setIsLoading(false);

          setShowSubmittedPage(true);
        });
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (data: IChangeEvent<FormData>) => {
    setFormData(data.formData);
  };

  const handleError = (errors) => {
    console.log("Form errors:", errors);
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="Contact Us" />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          backgroundColor: "#ffffff",
          border: "2px solid #000",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img
            src={img}
            alt="OptiMed Health"
            style={{
              maxWidth: "350px",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
          />
        </Box>
        <Box
          sx={{
            backgroundColor: "#1a365d",
            color: "white",
            py: 3,
            px: 4,
            mb: 4,
            textAlign: "center",
            fontSize: "1.75rem",
            fontWeight: 400,
            borderRadius: 2,
            width: "93%",
            mx: "auto",
          }}
        >
          Contact Us
        </Box>

        <Box sx={{ px: 4, pb: 4 }} className="cue-form">
          <Form
            schema={cueFormSchema}
            uiSchema={cueUiSchema}
            formData={formData}
            validator={validator}
            fields={fields}
            widgets={widgets}
            templates={templates}
            onChange={handleChange}
            onSubmit={handleSubmit}
            onError={handleError}
            showErrorList={false}
            liveValidate={false}
            formContext={{
              formData,
              updateFormData,
            }}
            className={styles["custom-form"]}
          >
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 4,
                  py: 1,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  minWidth: "150px",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Form>
        </Box>
      </Paper>
    </Container>
  );
}

export default CueForm;
